import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  selectedText: string;
  /** Viewport coordinates (fixed positioning); null centres the popover. */
  anchor: { x: number; y: number } | null;
  busy: boolean;
  onSubmit: (note: string) => void;
  onClose: () => void;
}

const PREVIEW_MAX = 240;

/** The note on a highlighted passage: what it says now, and how it should change. */
const SpanNotePopover: React.FC<Props> = ({ selectedText, anchor, busy, onSubmit, onClose }) => {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const canSubmit = note.trim() !== '' && !busy;
  const submit = () => {
    if (canSubmit) onSubmit(note.trim());
  };
  const preview =
    selectedText.length > PREVIEW_MAX ? `${selectedText.slice(0, PREVIEW_MAX)}…` : selectedText;

  const style: React.CSSProperties = anchor
    ? { position: 'fixed', left: `${anchor.x}px`, top: `${anchor.y}px` }
    : { position: 'fixed', left: '50%', top: '30%', transform: 'translateX(-50%)' };

  return (
    <div
      role="dialog"
      aria-label={t('article.span_note_title')}
      style={style}
      className="z-40 w-[26rem] max-w-[calc(100vw-2rem)] rounded-lg border border-gray-300 bg-white p-4 shadow-xl space-y-3"
      data-testid="span-note-popover"
    >
      <div className="text-sm font-semibold">{t('article.span_note_title')}</div>
      <div>
        <div className="text-xs text-gray-500 mb-1">{t('article.span_note_selected_label')}</div>
        <blockquote
          data-testid="span-note-selected"
          className="text-sm text-gray-700 border-l-2 border-amber-400 bg-amber-50 pl-3 py-1.5 whitespace-pre-wrap max-h-32 overflow-y-auto"
        >
          {preview}
        </blockquote>
      </div>
      <textarea
        ref={textareaRef}
        value={note}
        disabled={busy}
        rows={3}
        placeholder={t('article.span_note_placeholder')}
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
          } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            submit();
          }
        }}
        className="w-full p-2 border rounded-md text-sm disabled:bg-gray-50"
      />
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-3 py-1.5 border rounded-md text-sm hover:bg-gray-50 disabled:opacity-50"
        >
          {t('article.span_note_cancel')}
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50"
        >
          {busy ? t('article.span_note_working') : t('article.span_note_submit')}
        </button>
      </div>
    </div>
  );
};

export default SpanNotePopover;
